import { useCallback, useState } from 'react';
import useClientStatusContext from '../context/useClientStatusContext';
import { ClientStatus } from '../../../model/ClientStatus';
import { ClientResponse } from '../../../model/ClientResponse';
import { ClientStatusError } from '../../../model/ClientStatusError';

interface ReturnType<T> {
  wrapClient(...args): Promise<ClientResponse<T>>;
  data: T | null | undefined;
  status: ClientStatus;
}

type ClientCall<T> = (...args) => Promise<ClientResponse<T>>;

export default <T> (client: ClientCall<T>): ReturnType<T> => {
  const { handleStatus } = useClientStatusContext();
  const [status, setStatus] = useState<ClientStatus>({
    failed: false,
    finished: false,
    generalErrors: [],
    fieldErrors: [],
    loading: false,
    responseCode: undefined,
  });
  const [data, setData] = useState<T | null | undefined>(null);

  const wrapClient = useCallback(
    async (...args) => {
      setStatus(prevState => ({ ...prevState, loading: true }));

      try {
        const response = await client(...args);

        const newStatus = {
          responseCode: response.responseCode,
          loading: false,
          finished: true,
          generalErrors: [],
          fieldErrors: [],
          failed: false,
        };

        setData(response.data);
        setStatus(newStatus);
        handleStatus(newStatus);

        return Promise.resolve(response);
      } catch (e) {
        const generalResponseErrors: ClientStatusError[] = [];
        const fieldResponseErrors: ClientStatusError[] = [];

        if (e.response == null) {
          generalResponseErrors.push(
            { message: 'Nepodarilo sa vykonať akciu. Skúste neskôr.' },
          );
          const newStatus = {
            responseCode: 503,
            loading: false,
            finished: true,
            generalErrors: generalResponseErrors,
            fieldErrors: fieldResponseErrors,
            failed: true,
          };

          setData(null);
          setStatus(newStatus);
          handleStatus(newStatus);

          return Promise.reject();
        }

        const statusCode = e.response.status;
        let responseData = e.response.data;
        if (e.response.data instanceof Blob) {
          try {
            responseData = JSON.parse(await e.response.data.text());
            // eslint-disable-next-line no-empty
          } catch (ex) {
          }
        }

        responseData?.generalErrors?.forEach(error => generalResponseErrors.push(
          { message: error },
        ));

        responseData?.fieldErrors?.forEach(error => fieldResponseErrors.push(
          { message: error.message, fieldName: error.field },
        ));

        const newStatus = {
          responseCode: statusCode,
          loading: false,
          finished: true,
          generalErrors: generalResponseErrors,
          fieldErrors: fieldResponseErrors,
          failed: true,
        };

        setData(null);
        setStatus(newStatus);
        handleStatus(newStatus);

        return Promise.reject();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, handleStatus],
  );

  return {
    wrapClient,
    data,
    status,
  };
};
